import type { ScaleConfig, ValueWord } from '@anytracker/core'

import { IconButton } from '../../components/Button.js'
import { TextField } from '../../components/Field.js'
import styles from './TrackEditor.module.css'

interface Props {
  readonly scale: ScaleConfig
  readonly words: readonly ValueWord[]
  readonly onChange: (words: readonly ValueWord[]) => void
}

/**
 * Naming the points on a scale (T-807).
 *
 * **No design exists for this.** T-807 put authoring out of scope on the
 * grounds that no track editor was drawn at all, which was true of the first
 * handoff and is not true now — the editor exists, T-911 redraws it, and the
 * alternative was shipping words that could only arrive through a restored
 * backup. The shape here is inferred from the idiom the redesign already uses
 * for enum options: rows, plus a dashed "add another". Recorded in
 * `docs/design-issues.md`.
 *
 * Rows stay in the order they were added rather than sorting themselves by
 * position. A list that reorders under the cursor while a number is being typed
 * is unusable, and `wordFor` does not care about order.
 */
export function ValueWordsEditor({ scale, words, onChange }: Props) {
  function update(index: number, changes: Partial<ValueWord>) {
    onChange(words.map((word, at) => (at === index ? { ...word, ...changes } : word)))
  }

  /**
   * Where a new word starts.
   *
   * The midpoint for the first, then halfway between the last one and the top —
   * so adding three words on a 1–10 scale lands them somewhere plausible rather
   * than stacking them all at the minimum, which would be three validation
   * failures for the crime of pressing the button twice.
   */
  function nextPoint(): number {
    const highest = words.reduce((top, word) => Math.max(top, word.at), scale.min)
    const candidate =
      words.length === 0 ? (scale.min + scale.max) / 2 : (highest + scale.max) / 2
    return Number(candidate.toFixed(scale.decimals))
  }

  return (
    <div className={styles['words']}>
      <p className={styles['wordsHint']}>
        Optional. Name a few points and the logging screen shows the word beside the
        number — a screen reader reads it out too. You do not need one for every value:
        each word covers everything from its own point up to the next one.
      </p>

      {words.map((word, index) => (
        // Index as the key, deliberately: a word has no id, and two words can
        // legitimately hold the same text while one is being retyped — so
        // neither the label nor the point is stable enough to key on.
        <div key={index} className={styles['wordRow']}>
          <TextField
            label="At"
            type="number"
            inputMode="decimal"
            value={String(word.at)}
            onChange={(event) => {
              const parsed = Number(event.target.value)
              update(index, { at: Number.isFinite(parsed) ? parsed : word.at })
            }}
          />
          <TextField
            label="Word"
            value={word.label}
            placeholder="Rough, fine, good…"
            onChange={(event) => update(index, { label: event.target.value })}
          />
          <IconButton
            label={`Remove ${word.label.trim() === '' ? 'this word' : word.label}`}
            onClick={() => onChange(words.filter((_, at) => at !== index))}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" aria-hidden="true">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </IconButton>
        </div>
      ))}

      <button
        type="button"
        className={styles['addWord']}
        onClick={() => onChange([...words, { at: nextPoint(), label: '' }])}
      >
        + Add a word
      </button>
    </div>
  )
}
